import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import CartothequeDescriptionEditor from './CartothequeDescriptionEditor';
import SpeciesReferences from './SpeciesReferences';
import cardLabels, { cardSummary } from '../utils/cardLabels';
import extractText from 'flarum/common/utils/extractText';

// Mirrors champis-species' own SpeciesAutocompleteResult (js/src/common/
// speciesTypes.ts) - duplicated rather than imported, same as every other
// cross-extension shape in this codebase, since there's no build-time
// dependency on champis-species to import through. Kept to just the fields
// selectSpecies() below actually reads.
// CardNumberHints on the PHP side: `free` is the next free number of a card's
// base.

// CardSynonymsResource's rows: another name of the card's species.

// Lazy flarum.reg lookup, not a static top-level import - module evaluation
// order across extensions isn't guaranteed even within one concatenated
// forum.js. Same fix as champis-forum's DiscussionFilters.js and this
// extension's own ExhibitionFindingEditModal.js.
//
// Typed as `typeof Modal` (any real Component subclass works as a JSX tag
// type - Modal is already imported above) rather than Mithril.ComponentTypes:
// a bare ComponentTypes union has no construct/call signature TypeScript's
// own JSX checker recognizes, so `<SpeciesSearchWidget />` below fails to
// compile with it (confirmed directly - "does not have any construct or
// call signatures").
function getSpeciesSearchWidget() {
  return window.flarum.reg.get('champis-species', 'forum/components/SpeciesSearchWidget');
}
/**
 * Creates (or edits) one cartothèque card - attrs.society (required on
 * create) + attrs.entry (existing model, switches to edit mode) +
 * attrs.toxicities (the société's own Toxicity[], for the picker below).
 * No "undetermined" option - a cartothèque entry is always a real resolved
 * species, unlike ExhibitionFindingEditModal's own optional toggle.
 */
export default class CartothequeEntryEditModal extends Modal {
  gbifId = null;
  speciesName = null;
  // Its synonym's id ('' for none), the <select>'s value, among the species'
  // other names (loadSynonyms()).
  synonymGbifId = '';
  synonyms = [];
  // The other cards of that species, once asked (loadSpeciesTakenBy()).
  speciesTakenBy = [];
  no = '';
  vernacularName = '';
  toxicityId = '';
  toPrint = false;
  description = '';
  descriptionEditor = null;
  loading = false;
  errorMessage = null;
  // The other cards with the number typed, once asked (checkNumberSoon()).
  numberTakenBy = [];
  // What helps choose it, in a société that numbers its cards.
  numberHints = null;
  oninit(vnode) {
    super.oninit(vnode);
    const entry = this.attrs.entry;
    const display = entry && entry.speciesDisplay() || null;
    this.gbifId = entry && entry.gbifId() || null;
    this.speciesName = display ? display.canonicalName || display.scientificName : null;
    this.synonymGbifId = entry && entry.synonymGbifId() ? String(entry.synonymGbifId()) : '';
    this.synonyms = [];
    this.loadSynonyms();
    this.loadSpeciesTakenBy();
    this.no = entry && entry.no() || '';
    this.vernacularName = entry && entry.vernacularName() || '';
    const toxicity = entry && entry.toxicity();
    this.toxicityId = toxicity ? String(toxicity.id()) : '';
    this.toPrint = entry ? !!entry.toPrint() : false;
    this.description = entry && entry.description() || '';
    this.descriptionEditor = null;
    this.loading = false;
    this.errorMessage = null;
    this.numberHints = null;
    this.checkNumberSoon(0);
  }
  onremove(vnode) {
    super.onremove(vnode);
    clearTimeout(this.numberTimeout);
  }
  className() {
    return 'Modal--medium ChampisCartothequeEntryEditModal';
  }
  title() {
    const t = key => app.translator.trans(`champis-exhibitions.forum.cartotheque_entry_modal.${key}`);
    return this.attrs.entry ? t('edit_title') : t('create_title');
  }
  content() {
    const t = key => app.translator.trans(`champis-exhibitions.forum.cartotheque_entry_modal.${key}`);
    const SpeciesSearchWidget = getSpeciesSearchWidget();
    const toxicities = this.attrs.toxicities || [];
    const numberTakenBy = this.numberTakenBy;
    return m("div", {
      className: "Modal-body"
    }, this.errorMessage && m("div", {
      className: "ChampisCartothequeEntryEditModal-error"
    }, this.errorMessage), m("div", {
      className: "ChampisCartothequeEntryEditModal-field"
    }, m("label", null, t('species_label')), this.speciesName ? m("div", {
      className: "ChampisCartothequeEntryEditModal-selectedSpecies"
    }, m("em", null, this.speciesName), m("a", {
      onclick: () => {
        this.gbifId = null;
        this.speciesName = null;
        this.synonymGbifId = '';
        this.synonyms = [];
        this.speciesTakenBy = [];
        this.numberHints = null;
      }
    }, t('change_species'))) : SpeciesSearchWidget ? m(SpeciesSearchWidget, {
      placeholder: t('species_placeholder'),
      label: t('species_label'),
      hideLabel: true,
      onselect: species => this.selectSpecies(species)
    }) : null, this.gbifId && m(SpeciesReferences, {
      gbifId: this.gbifId,
      name: this.speciesName
    }), this.speciesTakenBy.length > 0 && m("div", {
      className: "ChampisCartothequeEntryEditModal-warning"
    }, m("i", {
      className: "icon fas fa-triangle-exclamation",
      "aria-hidden": "true"
    }), ' ', app.translator.trans('champis-exhibitions.forum.cartotheque_page.duplicate_existing', {
      cards: cardLabels(this.speciesTakenBy)
    }))), this.gbifId && m("div", {
      className: "ChampisCartothequeEntryEditModal-field"
    }, m("label", null, t('synonym_label'), m("select", {
      className: "FormControl",
      value: this.synonymGbifId,
      onchange: e => {
        this.synonymGbifId = e.target.value;
      }
    }, m("option", {
      value: ""
    }, t('synonym_none')), this.synonymOptions().map(option => m("option", {
      value: option.value
    }, option.label))))), m("div", {
      className: "ChampisCartothequeEntryEditModal-field"
    }, m("label", null, t('no_label'), m("input", {
      className: "FormControl",
      type: "text",
      placeholder: t('no_placeholder'),
      value: this.no,
      oninput: e => {
        this.no = e.target.value;
        this.checkNumberSoon();
      }
    })), numberTakenBy.length > 0 && m("div", {
      className: "ChampisCartothequeEntryEditModal-warning"
    }, m("i", {
      className: "icon fas fa-triangle-exclamation",
      "aria-hidden": "true"
    }), ' ', app.translator.trans('champis-exhibitions.forum.cartotheque_entry_modal.no_taken', {
      cards: cardLabels(numberTakenBy)
    })), this.numberHintsView()), m("div", {
      className: "ChampisCartothequeEntryEditModal-field"
    }, m("label", null, t('vernacular_name_label'), m("input", {
      className: "FormControl",
      type: "text",
      placeholder: t('vernacular_name_placeholder'),
      value: this.vernacularName,
      oninput: e => {
        this.vernacularName = e.target.value;
      }
    }))), m("div", {
      className: "ChampisCartothequeEntryEditModal-field"
    }, m("label", null, t('toxicity_label'), m("select", {
      className: "FormControl",
      value: this.toxicityId,
      onchange: e => {
        this.toxicityId = e.target.value;
      }
    }, m("option", {
      value: ""
    }, t('toxicity_none')), toxicities.map(tox => m("option", {
      value: tox.id()
    }, tox.name()))))), m("div", {
      className: "ChampisCartothequeEntryEditModal-field"
    }, m("label", {
      className: "ChampisCartothequeEntryEditModal-toPrint"
    }, m("input", {
      type: "checkbox",
      checked: this.toPrint,
      onchange: e => {
        this.toPrint = e.target.checked;
      }
    }), ' ', t('to_print_label'))), m("div", {
      className: "ChampisCartothequeEntryEditModal-field"
    }, m("label", null, t('description_label')), m("p", {
      className: "ChampisCartothequeEntryEditModal-descriptionHint"
    }, t('description_hint')), m(CartothequeDescriptionEditor, {
      value: this.description,
      onReady: editor => {
        this.descriptionEditor = editor;
      }
    })), m("div", {
      className: "ChampisCartothequeEntryEditModal-footer"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: () => this.submit(),
      loading: this.loading,
      disabled: !this.canSubmit()
    }, t('save_button'))));
  }
  selectSpecies(species) {
    // species.id is a JSON:API resource id (always a string) - this.gbifId
    // itself is a real number (sent as a numeric `gbifId` attribute on
    // submit below). Was silently a string-into-number assignment before
    // this file had any real type checking on it at all.
    this.gbifId = Number(species.id);
    this.speciesName = species.canonicalName || species.scientificName || null;
    this.synonymGbifId = '';
    this.synonyms = [];
    this.loadSynonyms();
    this.loadSpeciesTakenBy();
    this.checkNumberSoon(0);

    // Live prefill, only if the field is still empty - never overwrites a
    // value the user already typed/edited, same rule the server-side
    // CartothequeEntryResource::creating() fallback follows. Reuses
    // SpeciesResource's own existing opt-in vernacularName field, no new
    // PHP needed for this.
    if (!this.vernacularName) {
      app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/species/' + species.id,
        params: {
          vernacularName: 'true'
        }
      }).then(response => {
        if (this.vernacularName) return;
        const vernacularName = response.data && response.data.attributes && response.data.attributes.vernacularName;
        if (vernacularName) {
          this.vernacularName = vernacularName;
          m.redraw();
        }
      }).catch(() => {
        // Best-effort prefill only - species selection itself already
        // succeeded, and CartothequeEntryResource::creating() still
        // applies its own server-side fallback on save.
      });
    }
    m.redraw();
  }

  /** The species' other names - nothing to offer if that fails, the rest still saves. */
  loadSynonyms() {
    const gbifId = this.gbifId;
    if (!gbifId) return;
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/champis-exhibitions-card-synonyms',
      params: {
        filter: {
          gbifId
        }
      }
    }).then(response => {
      // Stale response for a species the user has since changed.
      if (gbifId !== this.gbifId) return;
      this.synonyms = (response.data || []).map(item => item.attributes);
      m.redraw();
    }).catch(() => {});
  }

  /**
   * The société's other cards of the species, under any of its names, asked
   * of the server as cardsWithNumber() - no warning if it can't say.
   */
  loadSpeciesTakenBy() {
    const gbifId = this.gbifId;
    this.speciesTakenBy = [];
    if (!gbifId) return;
    app.store.find('champis-exhibitions-cartotheque-entries', {
      filter: {
        society: this.attrs.society.id(),
        species: String(gbifId)
      },
      sort: 'number',
      page: {
        limit: 20
      }
    }, undefined, {
      errorHandler: () => {}
    }).then(entries => {
      // Stale response for a species the user has since changed.
      if (gbifId !== this.gbifId) return;
      this.speciesTakenBy = entries.filter(other => other.id() !== this.attrs.entry?.id()).map(cardSummary);
      m.redraw();
    }).catch(() => {});
  }

  /** The card's saved synonym too, until they're in (or if it's no longer among them). */
  synonymOptions() {
    const options = this.synonyms.map(name => ({
      value: String(name.gbifId),
      label: [name.canonicalName || name.scientificName, name.author].filter(Boolean).join(' ')
    }));
    const entry = this.attrs.entry;
    const saved = entry && entry.synonymDisplay();
    if (saved && this.synonymGbifId === String(entry.synonymGbifId()) && !options.some(option => option.value === this.synonymGbifId)) {
      options.unshift({
        value: this.synonymGbifId,
        label: [saved.canonicalName, saved.author].filter(Boolean).join(' ')
      });
    }
    return options;
  }
  canSubmit() {
    if (this.loading) return false;
    return !!this.gbifId;
  }

  /**
   * The cartothèque's other cards already numbered as typed - a number
   * identifies one card of a société's collection. Trimmed and
   * case-insensitive: "12a" is "12A". Asked of the server
   * (CartothequeEntryResource's filter[no]), CartothequePage having only
   * the cards scrolled to; none if it can't say.
   */
  cardsWithNumber() {
    const no = this.no.trim();
    if (!no) return Promise.resolve([]);
    return app.store.find('champis-exhibitions-cartotheque-entries', {
      filter: {
        society: this.attrs.society.id(),
        no
      },
      page: {
        limit: 20
      }
    }, undefined, {
      errorHandler: () => {}
    }).then(entries => entries.filter(other => other.id() !== this.attrs.entry?.id()).map(cardSummary)).catch(() => []);
  }

  // The warning under the number, once the typing stops.
  checkNumberSoon(delay) {
    if (delay === void 0) {
      delay = 300;
    }
    clearTimeout(this.numberTimeout);
    this.numberTimeout = window.setTimeout(() => {
      const no = this.no;
      this.cardsWithNumber().then(cards => {
        if (no !== this.no) return;
        this.numberTakenBy = cards;
        m.redraw();
      });
      this.loadNumberHints();
    }, delay);
  }

  /** Only helps: nothing shown if it fails, the card still saves. */
  loadNumberHints() {
    if (!this.attrs.society.cardWithNo()) return;
    const no = this.no;
    const gbifId = this.gbifId;
    const params = {};
    if (gbifId) params.gbifId = gbifId;
    if (no.trim()) params.no = no.trim();
    if (this.attrs.entry) params.entry = this.attrs.entry.id();
    if (!gbifId && !no.trim()) {
      this.numberHints = null;
      return;
    }
    app.request({
      method: 'GET',
      url: `${app.forum.attribute('apiUrl')}/champis-exhibitions-societies/${this.attrs.society.id()}/number-hints`,
      params,
      errorHandler: () => {}
    }).then(hints => {
      // Stale response for a number or species the user has since changed.
      if (no !== this.no || gbifId !== this.gbifId) return;
      this.numberHints = hints;
      m.redraw();
    }).catch(() => {});
  }
  useNumber(no) {
    this.no = no;
    this.checkNumberSoon(0);
  }

  /**
   * With no number yet, where the species' relatives are; with one typed,
   * what's under its base and the next free letter, the cards around it, and
   * a warning if that's far from the species' genus - the base being the
   * book's to say. Nothing about a card's number left as it was. See
   * CardNumberHints.
   */
  numberHintsView() {
    var _this = this;
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.cartotheque_entry_modal.no_hint_${key}`, params);
    };
    const hints = this.numberHints;
    if (!hints) return null;
    const useButton = no => m(Button, {
      className: "Button Button--primary ChampisCardNumberHints-use",
      onclick: () => this.useNumber(no)
    }, t('use', {
      no
    }));
    const chips = function (cards, genera) {
      if (genera === void 0) {
        genera = [];
      }
      return m("div", {
        className: "ChampisCardNumberHints-cards"
      }, cards.map(card => m("button", {
        type: "button",
        className: "ChampisCardNumberHints-card",
        title: extractText(t('pick', {
          no: card.free
        })),
        onclick: () => _this.useNumber(card.free)
      }, m("strong", null, card.no), " ", m("em", null, genera.reduce((name, genus) => name.replace(new RegExp(`^${genus} `), ''), card.name || '')))));
    };
    const no = this.no.trim();
    if (no) {
      const base = hints.base;
      if (!base || this.attrs.entry && no === this.attrs.entry.no()) return null;
      const typed = no.toLowerCase();
      const taken = base.cards.length > 0 && base.free !== typed;
      const genus = hints.genus;
      return m("div", null, base.farFromGenus && genus && m("div", {
        className: "ChampisCartothequeEntryEditModal-warning"
      }, m("i", {
        className: "icon fas fa-triangle-exclamation",
        "aria-hidden": "true"
      }), ' ', t(genus.from === genus.to ? 'far_one' : 'far', {
        genus: genus.names.join(', '),
        from: genus.from,
        to: genus.to
      })), (taken || base.around.length > 0 || typed === String(base.base)) && m("div", {
        className: "ChampisCardNumberHints"
      }, taken && m("div", null, m("div", {
        className: "ChampisCardNumberHints-title"
      }, t('base', {
        base: base.base
      })), chips(base.cards)), (taken || typed === String(base.base)) && base.free !== typed && m("div", {
        className: "ChampisCardNumberHints-suggestion"
      }, useButton(base.free)), base.around.length > 0 && m("div", null, m("div", {
        className: "ChampisCardNumberHints-title"
      }, t('around', {
        base: base.base
      })), chips(base.around))));
    }
    if (!hints.suggestion && !hints.genus && !hints.family) return null;
    return m("div", {
      className: "ChampisCardNumberHints"
    }, hints.suggestion && m("div", {
      className: "ChampisCardNumberHints-suggestion"
    }, useButton(hints.suggestion)), hints.sameSpecies.length > 0 && m("div", null, m("div", {
      className: "ChampisCardNumberHints-title"
    }, t('same_species')), chips(hints.sameSpecies)), hints.genus && m("div", null, m("div", {
      className: "ChampisCardNumberHints-title"
    }, t('genus', {
      genus: hints.genus.names.join(', ')
    })), chips(hints.genus.cards, hints.genus.names)), !hints.genus && hints.family && m("div", {
      className: "ChampisCardNumberHints-title"
    }, t('family', {
      family: hints.family.name,
      from: hints.family.from,
      to: hints.family.to,
      count: hints.family.count
    })));
  }
  submit() {
    this.loading = true;
    this.errorMessage = null;
    m.redraw();

    // A number already taken: shown under the field, and asked once more
    // here - two cards may share one on purpose, rarely. Asked again, in
    // case the warning is still on its way.
    this.cardsWithNumber().then(numberTakenBy => {
      if (numberTakenBy.length > 0) {
        const message = app.translator.trans('champis-exhibitions.forum.cartotheque_entry_modal.no_taken_confirm', {
          no: this.no.trim(),
          cards: cardLabels(numberTakenBy)
        });
        if (!confirm(extractText(message))) {
          this.loading = false;
          m.redraw();
          return;
        }
      }
      this.save();
    });
  }
  save() {
    // Reads the contenteditable's own live DOM state (see
    // CartothequeDescriptionEditor's own "uncontrolled by design"
    // docblock) - stripped-tags-empty (e.g. a lone "<br>" left behind by
    // an emptied-out editor) is treated the same as never having typed
    // anything, so an emptied description clears the card's QR code
    // rather than leaving a technically-non-null but visually blank page.
    const rawDescription = this.descriptionEditor ? this.descriptionEditor.getValue() : this.description;
    const description = rawDescription && rawDescription.replace(/<[^>]*>/g, '').trim() ? rawDescription : null;
    const attributes = {
      gbifId: this.gbifId,
      synonymGbifId: this.synonymGbifId ? Number(this.synonymGbifId) : null,
      no: this.no.trim() || null,
      vernacularName: this.vernacularName.trim() || null,
      toPrint: this.toPrint,
      description
    };
    const selectedToxicity = this.toxicityId ? (this.attrs.toxicities || []).find(tox => String(tox.id()) === String(this.toxicityId)) : undefined; // NOT null - Model.ts's save() silently drops a literal
    // null from the outgoing relationships payload entirely (the field's
    // .set() closure would never even run server-side), while undefined
    // correctly resolves to a real "clear this relationship" {data: null}
    // wire value via getIdentifier()'s own `!model` guard - confirmed by
    // reading Model.ts directly.

    const entry = this.attrs.entry || app.store.createRecord('champis-exhibitions-cartotheque-entries');
    // `toxicity` is deliberately `undefined`, not `null`, when nothing is
    // selected (see the comment above) - Model.ts's own SaveRelationships
    // type only declares `null | Model | Model[]`, so this is cast (through
    // `unknown`, bypassing the structural check) rather than widening that
    // exported type just for the one caller that relies on the
    // undefined/null distinction.
    const relationships = this.attrs.entry ? {
      toxicity: selectedToxicity
    } : {
      society: this.attrs.society,
      toxicity: selectedToxicity
    };
    entry.save({
      ...attributes,
      relationships
    }).then(() => {
      this.loading = false;
      this.hide();
      if (this.attrs.onsaved) this.attrs.onsaved(entry);
    }).catch(error => {
      this.loading = false;
      this.errorMessage = error && error.message || app.translator.trans('champis-exhibitions.forum.cartotheque_entry_modal.error');
      m.redraw();
    });
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/CartothequeEntryEditModal', CartothequeEntryEditModal);